import { computed, ref, unref, watch, type MaybeRef } from 'vue'

// composables
import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAccessStore } from '@/stores/AccessStore'
// types
import type { DealershipSelectOption, DealershipSelectSource } from '@/types/domain/dealership'
// utilities
import { toDealershipSelectOption } from '@/utilities/dealershipSelect'
import { alertToast } from '@/utilities/notification'

/** In-flight and resolved list-endpoint payloads, keyed by sorted dealership IDs */
const dealershipSelectCache = new Map<string, Promise<DealershipSelectSource[]>>()

/**
 * Clears the shared dealership-select fetch cache. Used by tests.
 */
export function resetDealershipSelectOptionsCache(): void {
  dealershipSelectCache.clear()
}

/**
 * Build a stable cache key from the dealership IDs being requested.
 *
 * @param ids - Dealership IDs included in the request
 * @returns Sorted comma-separated ID string
 */
function cacheKeyForIds(ids: number[]): string {
  return [...ids].sort((left, right) => left - right).join(',')
}

/**
 * Map access-allowed dealerships onto the selector source shape as a fallback
 * while the list endpoint loads (or if it fails). Tags are empty until the API returns.
 *
 * @param allowed - Access-allowed dealerships from the store
 * @param ids - IDs to include; when empty, all allowed dealerships are used
 * @returns Selector source rows
 */
function fallbackSourcesFromAccess(allowed: DealershipSelectSource[], ids: number[]): DealershipSelectSource[] {
  // No explicit ID filter means the full allowed pool
  if (ids.length === 0) {
    return allowed
  }

  const idSet = new Set(ids)
  return allowed.filter(dealership => idSet.has(dealership.id))
}

/**
 * Loads dealerships for DealershipSelect, including tags from the list endpoint.
 * Access-allowed dealerships are shown immediately so the trigger label is not empty
 * while the request is in flight.
 *
 * @param dealershipIds - Optional ID pool; defaults to the user's allowed dealerships
 * @returns Options, loading state, and a refresh function
 */
export function useDealershipSelectOptions(dealershipIds?: MaybeRef<number[] | undefined>) {
  const accessStore = useAccessStore()
  const dealershipsRequest = useApiRequest()
  const options = ref<DealershipSelectOption[]>([])
  const isLoading = ref(false)

  /**
   * Resolve which dealership IDs the selector should offer.
   *
   * @returns Explicit IDs when provided, otherwise every allowed dealership
   */
  function resolveRequestedIds(): number[] {
    const explicitIds = unref(dealershipIds)

    // Callers such as the calendar pass the location-selector subset
    if (Array.isArray(explicitIds)) {
      return explicitIds.filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
    }

    return accessStore.accessAllowed.dealerships.map(dealership => dealership.id)
  }

  /**
   * Seed options from the access store so grouping by org / manufacturer works immediately.
   *
   * @param ids - Dealership IDs to include
   */
  function applyFallbackOptions(ids: number[]): void {
    const allowed = accessStore.accessAllowed.dealerships as DealershipSelectSource[]
    options.value = fallbackSourcesFromAccess(allowed, ids).map(toDealershipSelectOption)
  }

  /**
   * Fetch dealerships from `/v1/dealerships` (the source of `tags`) and replace options.
   *
   * @param ids - Dealership IDs to request
   */
  async function fetchDealerships(ids: number[]): Promise<void> {
    // Nothing to offer when the user has no dealership access
    if (ids.length === 0) {
      options.value = []
      return
    }

    applyFallbackOptions(ids)

    const cacheKey = cacheKeyForIds(ids)
    let request = dealershipSelectCache.get(cacheKey)

    // Share in-flight and completed list requests across selector instances
    if (!request) {
      request = dealershipsRequest
        .send<DealershipSelectSource[]>({
          endpoint: '/v1/dealerships',
          params: { ids: ids.join(',') }
        })
        .then(response => {
          if (!response.success || !Array.isArray(response.data)) {
            dealershipSelectCache.delete(cacheKey)
            throw new Error(response.message || 'Failed to fetch dealerships')
          }

          return response.data
        })

      dealershipSelectCache.set(cacheKey, request)
    }

    isLoading.value = true

    try {
      const sources = await request
      const idSet = new Set(ids)
      // Keep the option list limited to the requested IDs even if the API returns extras
      options.value = sources.filter(source => idSet.has(source.id)).map(toDealershipSelectOption)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      alertToast('Failed to fetch dealerships', errorMessage, 'error')
      applyFallbackOptions(ids)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Re-fetch dealerships, bypassing the shared cache.
   */
  async function refresh(): Promise<void> {
    const ids = resolveRequestedIds()
    dealershipSelectCache.delete(cacheKeyForIds(ids))
    await fetchDealerships(ids)
  }

  watch(
    () => resolveRequestedIds().join(','),
    idsKey => {
      // Re-run when the allowed / selected ID pool changes
      void fetchDealerships(idsKey ? idsKey.split(',').map(Number) : [])
    },
    { immediate: true }
  )

  const hasTags = computed(() => options.value.some(option => option.tags.length > 0))

  return {
    options,
    isLoading,
    hasTags,
    refresh
  }
}
